export const contactsArticles = [
    {
        id: 'contacts.overview',
        title: 'Contacts Overview',
        category: 'Contacts',
        order: 1,
        summary: 'Managing your contact database',
        content: `
The Contacts page allows you to manage all your business contacts in one place, independent of suppliers or jobs.

**Contact Types:**

- Clients and prospects
- Architects and consultants
- Building inspectors
- General business contacts

**Key Features:**

- Maintain contact details
- Organize into groups
- Track correspondence
- Tag and categorize
    `,
        expandableSections: [
            {
                title: 'Contact Grid',
                content: `
The grid displays all contacts with:

- **Name** - Contact name
- **Company** - Associated company
- **Email/Phone** - Contact methods
- **Groups** - Group memberships
- **Tags** - Applied tags

Use filters and sorting to find specific contacts.
        `,
            },
        ],
        keywords: ['contacts', 'address book', 'people', 'database'],
        route: '/contacts',
        relatedArticles: ['contacts.add', 'contacts.groups'],
    },
    {
        id: 'contacts.add',
        title: 'Adding Contacts',
        category: 'Contacts',
        order: 2,
        summary: 'How to create new contact records',
        content: `
Create contact records for people you work with regularly.

**Contact Information:**

- Name / Company name
- Contact Person
- Contact Code (auto-generated or manual)
- Phone, Mobile, Email
- Physical address
- Contact type (Supplier, Client/Debtor, or General)
- Group assignment and tags
    `,
        expandableSections: [
            {
                title: 'Creating a Contact',
                content: `
1. Click **Add Contact** in the toolbar
2. Fill in the **Details** tab:
   - Enter the **Name / Company** name
   - Enter a **Contact Person** (optional)
   - Set the **Code** manually, or click the **magic wand** button to auto-generate from the name
   - Set contact type: tick Supplier and/or Client/Debtor, or leave as General
   - Assign to a **Group** (optional)
3. Fill in contact methods (Phone, Mobile, Email)
4. Fill in the **Address** section
5. Add **Tags** (comma-separated)
6. Click **Save**

**Auto-Generated Codes:**
Click the magic wand button next to the Code field to generate a unique code from the contact name. For example, "Smith Building Services" becomes "SBS001". If that code already exists, it increments to "SBS002", "SBS003", etc.
        `,
            },
            {
                title: 'Importing Contacts (CSV / Excel)',
                content: `
Bring in many contacts at once from a spreadsheet.

1. Click the **Import** button (upload icon) in the toolbar.
2. **Choose a file** — a \`.csv\` or \`.xlsx\` export, with column headers in the first row. (Click **Download template** for a starter file with the expected columns.)
3. **Map the columns** — Costed auto-matches obvious headers (Name, Email, Phone…); adjust any that are wrong from the dropdowns. **Name is required**; everything else is optional.
4. Set the options:
   - **Skip contacts that already exist** — matches on name or email so re-importing won't create duplicates.
   - **Create groups that don't exist yet** — otherwise unknown group names are left blank.
   - **Mark all as suppliers / customers** — or map a Supplier?/Customer? column with yes/no values.
5. Click **Import**. You'll get a summary of how many were **created**, **skipped** (already existed), and any **errors** with the row number and reason.

**What gets filled in:**
- **Code** — generated automatically for each contact (e.g. "Smith Building" → SMITHBU), unless you map a Code column. A supplied code that clashes is regenerated.
- **Type** — free text: whatever value you import is stored as-is (blank if empty). There is no fixed list of types. Supplier/Customer status comes from the Supplier?/Customer? columns or the "mark all" options.
- **Group** — matched to an existing group by name (case-insensitive). Unmatched names are left blank unless you tick **Create groups that don't exist yet**, which creates them.

**Managing groups:** click the **gear** button next to the *All Groups* filter to open **Manage Contact Groups** — add a group (name + colour), rename or recolour one inline, or delete it. Deleting a group ungroups its contacts (the contacts are kept, they just lose the group).

**Contact tags:** use the **Manage tags** button (tags icon) in the toolbar to add, rename, recolour or delete tags. Deleting a tag removes it from any contacts using it (the contacts are kept). Assign tags to a contact from its editor, or in bulk from the selection bar.

**Contact types:** open a contact and use the **Type** dropdown (Details tab) to categorise it. The list is controlled — click **Manage** beside the Type field to add, rename or delete types. Renaming a type updates every contact using it; deleting it clears the type on those contacts (the contacts are kept).
        `,
            },
            {
                title: 'Contact Types',
                content: `
Each contact can be flagged as one or more types:

| Type | Purpose |
|------|---------|
| **Supplier** | Appears in supplier lists, can receive purchase orders |
| **Client / Debtor** | Appears in client lists, can be assigned to jobs |
| **General** | Standard contact with no special role |

A contact can be both Supplier and Client simultaneously.
        `,
            },
        ],
        keywords: ['add contact', 'new contact', 'create contact', 'auto code', 'generate code'],
        route: '/contacts',
        relatedArticles: ['contacts.overview', 'contacts.groups'],
        screenshots: [
            {
                src: 'contacts/contacts-add.png',
                alt: 'New Contact form',
                caption: 'The New Contact form — pick the contact type, then fill in the details',
            },
        ],
    },
    {
        id: 'contacts.groups',
        title: 'Contact Groups',
        category: 'Contacts',
        order: 3,
        summary: 'Organizing contacts into groups',
        content: `
Groups help organize contacts for easy filtering and bulk operations.

**Example Groups:**

- Clients
- Architects
- Consultants
- Council Inspectors
- Internal Staff
    `,
        expandableSections: [
            {
                title: 'Managing Groups',
                content: `
**Creating Groups:**
1. Click the Groups panel
2. Click **Add Group**
3. Enter group name
4. Save

**Assigning Contacts:**
- Edit contact and select groups
- Or drag contact to group in panel
- Or select contacts and bulk assign
        `,
            },
        ],
        keywords: ['groups', 'categories', 'organize', 'filter'],
        route: '/contacts',
        relatedArticles: ['contacts.overview', 'contacts.add'],
    },
];
